import { useEffect, useRef, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { services } from '@/services';
import { isSameOriginPath } from '@/lib/safeUrl';
import { ApiHttpError } from '@/types';
import { renderGoogleSignInButton } from '@/services/google';
import { BootSplash } from '@/components/BootSplash';
import { bootStatusKeys } from '@/components/bootStatus';
import { looseListT } from '@/i18n/looseT';
import { useBootElapsedMs } from '@/components/useBootElapsedMs';
import { useAuthStore } from '@/stores/authStore';
import { DolphinMark } from '@/components/v2';

/**
 * 登入頁
 * -----------------------------------------------------------------------------
 * 流程：
 *
 * 1. 掛載時：若 store 已有 ``user``，直接 ``<Navigate to="/home" replace />``，
 *    避免使用者按瀏覽器「上一頁」回到登入畫面。
 * 2. ``useEffect``：在 ``divRef`` 上 render Google 官方 sign-in 按鈕。
 * 3. 使用者點按鈕後 GIS 透過 callback 回傳 ``id_token``：
 *    a. 呼叫 ``services.authProvider.signIn(idToken)`` 換我家 JWT。
 *    b. 把回傳的 ``LoginResult`` 寫進 ``useAuthStore``。
 *    c. 用 ``navigate(from, { replace: true })`` 帶回原本要去的路徑（沒記錄就 ``/home``）。
 * 4. 任何步驟失敗 → 設 ``error`` state；UI 以 i18n 訊息顯示，並允許重試。
 *
 * 設計備註：
 * - ``client_id`` 跟後端 ``GET /api/front_auth/google/config`` 拿；後端再從
 *   permission-service 拿、cache 1 小時。前端不再讀 ``VITE_GOOGLE_CLIENT_ID``，
 *   dev/staging/prod 可以用同一份 build。
 * - 不阻擋按鈕初始化的 Promise；任一步失敗（拿不到 client_id / SDK 載入失敗）
 *   都把錯誤吃回 ``error`` state 顯示給使用者，並允許重試。
 * - 不在這裡呼叫 ``finishHydrating()``；那是 :func:`useAuthBootstrap` 的責任。
 *
 * @example
 *   <Route path="/login" element={<LoginPage />} />
 */
export function LoginPage() {
  const { t } = useTranslation(['login', 'common']);
  const navigate = useNavigate();
  const location = useLocation();
  const buttonRef = useRef<HTMLDivElement | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  /**
   * GIS 按鈕**畫出來了沒**。R317 —— 在它變 true 之前,下面那個掛 ref 的 div 是
   * **完全空的**:卡片標題與說明都在,底下卻什麼都沒有,而 Cloud Run 冷啟動時
   * 跟後端要 client_id 可能等好幾秒 → 使用者看到的是「這頁壞了」。
   *
   * ⚠️ 判準是「**按鈕真的畫好了**」(GIS render 的 promise resolve)而不是
   * 「請求送出了」—— GIS 是把 iframe 注進那個 div,config 拿到之後還有一段。
   */
  const [googleReady, setGoogleReady] = useState(false);

  const user = useAuthStore((s) => s.user);
  const hydrating = useAuthStore((s) => s.hydrating);
  const setSession = useAuthStore((s) => s.setSession);

  // R224 資安 — 登入後導回目標必須過 same-origin 守門。
  //
  // 供給端(`RequireAuth`)今天只塞 `location.pathname`,所以正常流程一定同源;但這是
  // **導覽 sink 吃路由 state**,而 state 不是不可偽造的(`history.pushState` / 之後有人
  // 換供給端 / 相依的 react-router 6.x 線帶著 open-redirect CVE 且**無修補版**)。
  // `pnpm audit` 那三條 moderate 的唯一實際 sink 就是這一行 —— 今天不可利用是靠
  // 「catch-all route 沒有包 RequireAuth」的路由配置巧合,不是靠判準。把巧合換成判準。
  const fromState = (location.state as { from?: string } | null)?.from;
  const redirectTo =
    isSameOriginPath(fromState) && fromState !== '/login' ? fromState : '/home';

  useEffect(() => {
    if (hydrating || user) return;
    const target = buttonRef.current;
    if (!target) return;

    let cancelled = false;

    const handleCredential = async (idToken: string): Promise<void> => {
      if (cancelled) return;
      setError(null);
      setSubmitting(true);
      try {
        const result = await services.authProvider.signIn(idToken);
        if (cancelled) return;
        setSession(result);
        navigate(redirectTo, { replace: true });
      } catch (err) {
        if (cancelled) return;
        const message =
          err instanceof ApiHttpError && err.status === 401
            ? t('login:errors.sessionExpired')
            : t('login:errors.googleFailed');
        setError(message);
      } finally {
        if (!cancelled) setSubmitting(false);
      }
    };

    // 1. 跟後端拿 client_id（後端 cache 1 小時，多次掛載不會打爆 permission-service）
    // 2. 拿到後才 render GIS 按鈕；按鈕 callback 裡走 handleCredential 換 session
    void (async () => {
      try {
        const { clientId } = await services.authProvider.getGoogleConfig();
        if (cancelled) return;
        await renderGoogleSignInButton(target, clientId, handleCredential, {
          locale: 'zh_TW',
          theme: 'outline',
          size: 'large',
          text: 'signin_with',
          shape: 'rectangular',
        });
        if (cancelled) return;
        setGoogleReady(true);
      } catch (err: unknown) {
        if (cancelled) return;
        // R112:GIS / config 內部錯誤訊息是 developer-facing(英文),不直接上 UI;
        // 統一顯 i18n 文案,原始錯誤留 console 供診斷。
        console.error('[login] Google Sign-In bootstrap failed', err);
        setError(t('login:errors.googleFailed'));
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [hydrating, user, navigate, redirectTo, setSession, t]);

  if (hydrating) {
    // R317 — 與 RequireAuth 那兩道 gate 同一種情況(等 auth bootstrap),用同一個畫面。
    // 原本這裡是一句不動的「載入中⋯⋯」,冷啟動下十幾秒不變 = 讀起來像當掉。
    return <BootSplash phase="auth" />;
  }

  if (user) {
    return <Navigate to={redirectTo} replace />;
  }

  return (
    <main className="relative min-h-screen overflow-hidden bg-ink-950 text-ink-300">
      {/* 海洋風背景：靜態 radial gradient + 一隻淡海豚剪影。
          設計原型當年有 18 顆氣泡 + SVG 海浪 morphing 動畫，基於「動工最小」原則砍掉
          所有動畫只留靜態裝飾（原型檔已隨 R281 從 repo 移除）。 */}
      <div
        aria-hidden
        className="absolute inset-0 -z-10"
        style={{
          background:
            'radial-gradient(120% 80% at 50% 0%, var(--login-hero-glow), var(--ink-950) 60%)',
        }}
      />
      <div
        aria-hidden
        className="absolute"
        style={{ left: '14%', top: '22%', opacity: 0.18, transform: 'rotate(-6deg)' }}
      >
        <DolphinMark size={220} color="var(--tide-400)" />
      </div>

      {/* 左上品牌列 */}
      <div className="absolute left-8 top-7 z-10 flex items-center gap-3">
        <DolphinMark size={26} color="var(--tide-400)" />
        <span className="text-[length:var(--text-lg)] font-semibold text-ink-100">HAi</span>
        <span className="font-mono text-[length:var(--text-sm)] text-ink-500">{t('login:brandLine')}</span>
      </div>

      {/* 玻璃登入卡 */}
      <section
        className="absolute left-1/2 top-1/2 z-10 w-[460px] max-w-[calc(100vw-2rem)] -translate-x-1/2 -translate-y-1/2 rounded-xl border border-hairline-strong bg-glass-strong px-10 pb-8 pt-11 shadow-token-lg"
        style={{
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          isolation: 'isolate',
        }}
      >
        <div className="mb-4 text-[length:var(--text-xs)] font-medium uppercase tracking-[0.14em] text-[color:var(--concept-fg)]">
          {t('login:kicker')}
        </div>
        <h1 className="m-0 text-[length:var(--text-3xl)] font-medium leading-[1.15] tracking-[-0.02em] text-ink-100">
          {t('login:title')}
        </h1>
        <p className="mb-7 mt-4 text-[length:var(--text-md)] leading-relaxed text-ink-300">{t('login:subtitle')}</p>

        <div className="flex flex-col items-center gap-3">
          {/* GIS 會把按鈕注進這個 div;畫好之前它是 0 高,由下面的佔位撐住版面 */}
          <div ref={buttonRef} aria-label={t('login:signInWithGoogle')} />
          {!googleReady && !error && <GoogleButtonPlaceholder />}
          {submitting && <SigningInLine />}
          {error && (
            <p role="alert" className="text-[length:var(--text-sm)] text-[color:var(--rose-700)]">
              {error}
            </p>
          )}
        </div>

        <p className="mt-6 text-center text-[length:var(--text-xs)] leading-relaxed text-ink-500">
          {t('login:privacy')}
        </p>
      </section>
    </main>
  );
}

/**
 * Google 登入按鈕的等待佔位(R317)
 * -----------------------------------------------------------------------------
 * owner 2026-09-11:「沒有等待的感覺,緩啟動的時候(Google 還沒給的時候)」。
 *
 * ⚠️ **高度要對齊真按鈕(40px)**:GIS `size:'large'` 畫出來就是 40px 高,佔位矮了
 * 按鈕出現時整張卡會往下跳一格 —— 載入中的版面跳動比沒有提示更煩。
 *
 * ⚠️ **文案走 `bootStatusKeys`**(與 R314 冷啟動 splash 同一套):等的是同一件事
 * (後端冷啟動),所以升級門檻(4s / 12s)與措辭都共用,不要在這裡另寫一套。
 * 那套的紅線也一起繼承:**只描述可能性、不做診斷、不編進度%**。
 *
 * ⚠️ **不要用 `aria-live`**:上面那個掛 ref 的 div 已經帶 `aria-label`,這裡再開一個
 * live region 會讓讀屏在按鈕出現時唸兩次。純視覺提示 + `aria-hidden` 交給按鈕本身說話。
 */
function GoogleButtonPlaceholder() {
  // ⚠️ 這裡刻意用**單一** `common` namespace(不是 LoginPage 那個 `['login','common']`)——
  // `bootStatusKeys` 回的是無前綴的 `boot.*`,多 namespace 的 `t` 需要 `common:` 前綴才吃,
  // 分開拿比在每個 key 前面拼字串安全(拼錯只會顯示 raw key,不會編譯失敗)。
  const { t } = useTranslation('common');
  const { titleKey, hintIndex } = bootStatusKeys('google', useBootElapsedMs());
  const hint = hintIndex === null ? null : looseListT(t)<string>('boot.hints')[hintIndex];
  return (
    <div className="flex flex-col items-center gap-2" data-testid="login-google-pending">
      <div
        aria-hidden
        className="flex h-10 w-[240px] items-center justify-center gap-2 rounded border border-hairline bg-glass"
      >
        <span
          className="inline-block h-4 w-4 rounded-full border-2 border-hairline-strong"
          style={{ borderTopColor: 'var(--tide-400)', animation: 'spin 0.9s linear infinite' }}
        />
        <span className="text-[length:var(--text-sm)] text-ink-500">{t(titleKey)}</span>
      </div>
      {/* ⚠️ 固定高度:10 句長短不一,不固定的話每 3 秒整張卡會上下抽動 */}
      <p className="min-h-[32px] max-w-[280px] text-center text-[length:var(--text-xs)] leading-relaxed text-ink-500">
        {hint}
      </p>
    </div>
  );
}

/**
 * 換 token 期間的狀態列(R317)—— 按下 Google 按鈕之後、拿到我家 session 之前。
 *
 * 原本這裡也是那句通用的「載入中⋯⋯」。這一段是**明確的一件事**(在完成登入),
 * 講清楚比講通用的好;而且它同樣可能撞後端冷啟動,所以沿用同一套升級門檻。
 */
function SigningInLine() {
  const { t } = useTranslation('common');
  const { titleKey, hintIndex } = bootStatusKeys('signin', useBootElapsedMs());
  const hint = hintIndex === null ? null : looseListT(t)<string>('boot.hints')[hintIndex];
  return (
    <p
      role="status"
      aria-live="polite"
      data-testid="login-signing-in"
      className="text-center text-[length:var(--text-sm)] text-ink-400"
    >
      {t(titleKey)}
      <span className="mt-1 block min-h-[18px] text-[length:var(--text-xs)] text-ink-500">
        {hint}
      </span>
    </p>
  );
}
